<script setup lang="ts">
import {
	Input,
	SingleSelectList,
	SingleSelectListItem,
	Skeleton,
} from '@laam/ui/base';
import { PhMagnifyingGlass, PhX } from '@phosphor-icons/vue';
import { vElementVisibility } from '@vueuse/components';
import CartButton from '~/components/CartButton.vue';
import {
	getFilteredList,
	getGroupByInitial,
} from '~/components/catalogue/utils';
import { useFetchAllCollections } from '~/data/collections';

const { $eventClient } = useNuxtApp();

const searchQuery = ref('');
const selectedGroup = ref('');

const { data: collections, isLoading } = useFetchAllCollections();

const filteredItemsList = computed(() =>
	getFilteredList(collections.value, searchQuery.value, 'title'),
);
const initialsGroup = computed(() =>
	getGroupByInitial(filteredItemsList.value, 'title'),
);
function groupedList(initial: string) {
	const list = filteredItemsList.value.filter((collection) =>
		collection.title.toLowerCase().startsWith(initial.toLowerCase()),
	);
	return list;
}

function handleChange(value: string) {
	const element = document.getElementById(value);
	if (element) {
		const offset = isMobileView() ? -140 : -254;
		const elementPosition = element.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.pageYOffset + offset;

		window.scrollTo({
			top: offsetPosition,
			behavior: 'instant',
		});
	}
	selectedGroup.value = value;
}

function checkSticky(state: boolean, initial: string) {
	if (state) {
		selectedGroup.value = initial;
	}
}

const handleClick = (collection: {
	collectionName: string;
	collectionHandle: string;
}) => {
	$eventClient.sendEvent('collection-click', {
		collectionName: collection.collectionName,
		collectionHandle: collection.collectionHandle,
		from: window.location.href,
	});
};

const scrollToSelected = () => {
	nextTick(() => {
		const selectedElement = document.getElementById(
			`single-select-${selectedGroup.value}`,
		);
		if (selectedElement) {
			selectedElement.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}
	});
};

watch(selectedGroup, () => {
	scrollToSelected();
});

onMounted(() => {
	scrollToSelected();
});
</script>
<template>
	<div v-if="!isLoading">
		<div v-if="!isMobileView()" class="flex flex-col">
			<!-- header -->
			<div class="relative">
				<div class="flex flex-col gap-7xl bg-white pt-10xl fixed z-10 w-[52vw]">
					<div class="flex justify-between items-center">
						<p class="truncate text-gray-800 font-semibold text-2xl">
							Discover All Collections
						</p>
						<Input
							v-model="searchQuery"
							size="md"
							class="w-[30%]"
							placeholder="Search for Collections"
						>
							<template #leftIcon>
								<PhMagnifyingGlass />
							</template>
							<template v-if="searchQuery" #rightIcon>
								<PhX
									size="20"
									class="hover:cursor-pointer"
									@click="searchQuery = ''"
								/>
							</template>
						</Input>
					</div>
					<!-- alphabets list -->
					<SingleSelectList
						class="py-3xl flex justify-between border-b border-gray-200 w-full flex-row overflow-x-scroll"
						default-value="recommended"
						:model-value="selectedGroup"
						@update:model-value="handleChange"
					>
						<SingleSelectListItem
							v-for="collectionInitial in initialsGroup"
							:id="`single-select-${collectionInitial}`"
							:key="collectionInitial"
							:value="collectionInitial"
							class="flex flex-col items-center rounded-md h-8xl w-8xl !p-sm aspect-square text-gray-800 bg-white data-[state=checked]:text-white data-[state=checked]:bg-gray-800 text-sm font-semibold"
						>
							{{ collectionInitial }}
						</SingleSelectListItem>
					</SingleSelectList>
				</div>
			</div>
			<!-- current alphabet -->
			<div class="mt-[150px] z-1">
				<div
					v-for="(collectionInitial, index) in initialsGroup"
					:id="collectionInitial"
					:key="index"
					:v-el="collectionInitial"
				>
					<div class="pb-5xl">
						<div
							v-element-visibility="[
								(state: boolean) => checkSticky(state, collectionInitial),
								{ threshold: 1 },
							]"
							class="flex pb-3xl flex-col border-b border-gray-200 sticky top-[275px] bg-white pt-7xl"
						>
							<div
								class="flex p-3xl flex-col items-center justify-center gap-lg rounded-medium bg-gray-100 w-[60px]"
							>
								<p class="text-gray-800 text-center text-xl font-semibold">
									{{ collectionInitial }}
								</p>
							</div>
						</div>
						<!-- collections -->
						<ul class="collection-list pt-3xl grid-container">
							<li
								v-for="(collection, index2) in groupedList(collectionInitial)"
								:key="index2"
								class="grid-item"
							>
								<NuxtLink
									class="w-full"
									:to="`/collections/${collection.handle}`"
									@click="
										handleClick({
											collectionName: collection.title,
											collectionHandle: collection.handle,
										})
									"
								>
									<div
										class="flex py-xl px-4xl items-center gap-sm rounded-medium hover:cursor-pointer hover:bg-gray-50"
									>
										<p class="text-gray-800 text-md">
											{{ collection.title }}
										</p>
									</div>
								</NuxtLink>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
		<div v-else>
			<div class="flex flex-col">
				<div class="flex flex-col fixed w-[100vw] bg-white">
					<div class="flex py-md pr-md px-3xl items-center gap-md">
						<Input v-model="searchQuery" placeholder="Search for collections">
							<template #leftIcon>
								<PhMagnifyingGlass />
							</template>
							<template v-if="searchQuery" #rightIcon>
								<PhX size="20" @click="searchQuery = ''" />
							</template>
						</Input>
						<CartButton />
					</div>
					<SingleSelectList
						class="py-3xl flex justify-between border-b border-gray-200 w-full flex-row overflow-x-auto pl-3xl"
						default-value="recommended"
						:model-value="selectedGroup"
						@update:model-value="handleChange"
					>
						<SingleSelectListItem
							v-for="initial in initialsGroup"
							:key="initial"
							:value="initial"
							class="flex flex-col items-center rounded-md h-8xl w-8xl !p-sm aspect-square text-gray-800 bg-white data-[state=checked]:text-white data-[state=checked]:bg-gray-800 text-sm font-semibold"
						>
							{{ initial }}
						</SingleSelectListItem>
					</SingleSelectList>
				</div>
				<div class="flex p-3xl flex-col mt-[127px]">
					<div
						v-for="(initial, index) in initialsGroup"
						:id="initial"
						:key="index"
						:v-el="initial"
					>
						<div
							v-element-visibility="[
								(state: boolean) => checkSticky(state, initial),
								{ threshold: 1 },
							]"
							:class="{
								'pt-5xl': index != 0,
							}"
						>
							<div
								class="flex p-xl flex-col justify-center items-center gap-lg rounded-medium bg-gray-100 w-[52px]"
							>
								<p class="text-gray-800 text-center text-lg font-semibold">
									{{ initial }}
								</p>
							</div>
						</div>
						<ul class="collection-list">
							<li
								v-for="(collection, index2) in groupedList(initial)"
								:key="index2"
							>
								<NuxtLink
									class="w-full"
									:to="`/collections/${collection.handle}`"
								>
									<div class="flex py-3xl border-b border-gray-200">
										<p class="text-gray-800 text-md">
											{{ collection.title }}
										</p>
									</div>
								</NuxtLink>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div v-else class="collections--loader lg:mt-[100px]">
		<div
			class="flex h-[56px] w-full bg-white border-b py-md pl-3xl pr-md gap-3xl lg:mt-0 mt-[56px]"
		>
			<Skeleton class="w-full h-full col-span-3 bg-gray-300" />
			<Skeleton class="bg-gray-400 size-9xl" />
		</div>
		<div class="flex border-b gap-md px-3xl py-md">
			<Skeleton class="bg-gray-800 size-8xl rounded-medium" />
			<Skeleton
				v-for="i in 8"
				:key="i"
				class="bg-gray-300 size-8xl rounded-medium"
			/>
		</div>
		<div class="p-3xl">
			<div v-for="i in 5" :key="i">
				<Skeleton class="bg-gray-100 rounded w-9xl h-9xl" />
				<Skeleton
					class="ml-5xl pl-6xl px-3xl mx-3xl my-3xl py-lg block w-[90%] lg:w-fit border-l-2 border-r-0 border-gray-100"
				/>
			</div>
		</div>
	</div>
</template>

<style scoped>
.grid-container {
	display: grid;
	grid-template-columns: repeat(2, 1fr); /* Creates two equal columns */
	gap: 16px; /* Adjust this to control the space between items */
}

.grid-item {
	width: 100%;
}

.collection-list-initials::-webkit-scrollbar {
	@apply hidden;
}

.component--collection-list-subheader::-webkit-scrollbar {
	@apply lg:hidden;
}
</style>
